// Rendu HTML du DIFF « avant / après » adaptation (backlog P3 — D1 + D2).
//
// On réutilise le moteur CSS du CV normal (buildCss / resolveRenderSettings)
// pour rester visuellement cohérent avec la preview, puis on SURCOUCHE des
// styles de diff. Le document est volontairement READ-ONLY (pas d'édition) :
// c'est une vue d'inspection, pas le document exporté.
//
//  - Texte AJOUTÉ      -> surligné vert (.diff-add)
//  - Texte SUPPRIMÉ    -> barré rouge (.diff-removed)
//  - Texte REFORMULÉ   -> surligné jaune cliquable (.diff-reword) : un clic
//                         révèle « Avant : … » (D2, source highlighting).
//  - Texte INCHANGÉ    -> neutre.
//
// Seules les sections où l'IA TRAVAILLE sont rendues (accroche, compétences,
// expériences). Formation / langues / coordonnées sont conservées telles
// quelles par le pipeline : aucun diff à montrer, on évite le bruit.
//
// MAIS le masquage de sections du panneau live s'applique quand même : cette
// vue décrit le document qu'on va produire, et une section qui n'y sera pas n'a
// pas de diff à montrer. C'était le seul rendu du dépôt qui ignorait
// `settings.sections` — décocher « Phrase d'intro » n'avait donc aucun effet
// tant que la bascule « comparer » était active, sans que rien ne le dise.
//
// Même raison pour le `<title>` : le bouton « Télécharger PDF » reste actif en
// vue diff, et c'est de ce titre que Chrome tire le nom du fichier. Il sort donc
// de `documentName.ts` comme les autres, suffixé `_diff` pour ne pas écraser le
// CV dans le dossier Téléchargements.

import type { ResumeProfile } from './schema'
import { documentBaseName } from './documentName'
import {
  buildCss,
  escapeHtml,
  resolveRenderSettings,
  SECTION_TITLES,
  type RenderSettings,
} from './resumeHtml'
import { computeResumeDiff, type BulletTrace, type ResumeDiff, type SummaryDiff } from './resumeDiff'

// CSS de diff surajouté au CSS du CV. Couleurs sobres, contrastées à l'impression.
const DIFF_CSS = `
  .diff-legend {
    display: flex; flex-wrap: wrap; gap: 10px;
    font-size: 9pt; color: #555;
    margin: 0 0 12px 0; padding-bottom: 8px;
    border-bottom: 1px dashed #ccc;
  }
  .diff-legend span { display: inline-flex; align-items: center; gap: 4px; }
  .diff-chip { width: 11px; height: 11px; border-radius: 2px; display: inline-block; }
  .diff-chip.add { background: #b6e3c2; }
  .diff-chip.reword { background: #fde68a; }
  .diff-chip.removed { background: #f3b6b6; }

  .diff-add { background: #e6f4ea; box-shadow: inset 0 0 0 1px #b6e3c2; border-radius: 2px; padding: 0 2px; }
  .diff-reword {
    background: #fff7d6; box-shadow: inset 0 0 0 1px #fde68a; border-radius: 2px;
    padding: 0 2px; cursor: pointer;
  }
  .diff-reword::after {
    content: '↻'; font-size: 0.8em; color: #92750a; margin-left: 4px; opacity: 0.8;
  }
  .diff-removed {
    color: #b71c1c; text-decoration: line-through;
    background: #fdecec; border-radius: 2px; padding: 0 2px;
  }
  .diff-src {
    display: none;
    margin: 3px 0 0 0; padding: 4px 8px;
    font-size: 9.5pt; font-style: italic; color: #6b6b6b;
    background: #f6f6f6; border-left: 2px solid #fde68a; border-radius: 2px;
  }
  .diff-reword.open + .diff-src, li.open .diff-src { display: block; }
  .diff-src-label { font-style: normal; font-weight: 600; color: #92750a; }

  /* Bouton « rétablir », accroché sur chaque puce reformulée par l'app hôte
     (la console aujourd'hui, l'extension le jour où elle l'exposera).
     Son apparence est ICI et pas chez l'appelant : le reste du diff est stylé
     par cette feuille, et un bouton habillé en JavaScript par chaque app
     donnerait deux boutons différents dans le même document.
     Couleurs en dur assumées : ce document est une feuille A4 toujours
     blanche (impression, PDF), il ne suit pas le thème de l'application. */
  .revert-btn {
    margin-left: 8px;
    padding: 2px 7px;
    font: inherit;
    font-size: 12px;
    line-height: 1.3;
    vertical-align: middle;
    color: #5a6b82;
    background: transparent;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    cursor: pointer;
  }
  .revert-btn:hover { color: #1d2226; border-color: #94a3b8; }
  .revert-btn[disabled] { opacity: .55; cursor: progress; }
`

// Petit script D2 : un clic sur une puce reformulée révèle/masque sa source.
// Inline (comme le bridge de révision) pour rester autoporteur dans l'iframe.
const DIFF_SCRIPT = `
(function () {
  document.addEventListener('click', function (e) {
    var t = e.target;
    while (t && t !== document.body) {
      if (t.classList && t.classList.contains('diff-reword')) {
        var li = t.closest('li');
        if (li) li.classList.toggle('open');
        else t.classList.toggle('open');
        return;
      }
      t = t.parentNode;
    }
  });
})();
`

/** Libellés de la légende — passés par l'appelant pour rester i18n (sinon FR). */
export interface DiffLabels {
  added: string
  reworded: string
  removed: string
  before: string // préfixe « Avant : »
}

const DEFAULT_LABELS: DiffLabels = {
  added: 'Ajouté',
  reworded: 'Reformulé (cliquez)',
  removed: 'Retiré',
  before: 'Avant :',
}

// Rend une puce adaptée annotée + (si reformulée) sa source repliée.
//
// Les attributs `data-work` / `data-bullet` rendent chaque puce ADRESSABLE
// depuis le parent : c'est ce qui permet à une app d'accrocher une action sur
// une phrase précise (rétablir sa version d'origine) sans réimplémenter le
// rendu. Ils indexent `diff.work[w].bullets[b]`.
function renderBullet(b: BulletTrace, labels: DiffLabels, workIndex: number, bulletIndex: number): string {
  const text = escapeHtml(b.text)
  const at = `data-work="${workIndex}" data-bullet="${bulletIndex}"`
  if (b.change === 'added') {
    return `<li ${at} data-change="added"><span class="diff-add">${text}</span></li>`
  }
  if (b.change === 'reworded' && b.sourceText) {
    return `<li ${at} data-change="reworded"><span class="diff-reword" title="${labels.before} ${escapeHtml(b.sourceText)}">${text}</span>` +
      `<div class="diff-src"><span class="diff-src-label">${labels.before}</span> ${escapeHtml(b.sourceText)}</div></li>`
  }
  return `<li ${at} data-change="unchanged">${text}</li>`
}

function renderRemoved(text: string): string {
  return `<li><span class="diff-removed">${escapeHtml(text)}</span></li>`
}

/**
 * Rend le diff complet en HTML autonome (doctype/head/style), prêt pour
 * `iframe.srcdoc`. Recalcule le diff en interne pour une API à 2 entrées ;
 * le calcul est local et négligeable.
 */
export function renderResumeDiffToHTML(
  master: ResumeProfile,
  adapted: ResumeProfile,
  settings?: RenderSettings | unknown,
  labels: DiffLabels = DEFAULT_LABELS,
): string {
  const diff = computeResumeDiff(master, adapted)
  return renderDiffHtml(adapted, diff, settings, labels)
}

/** Variante : rend depuis un diff DÉJÀ calculé (évite le double calcul quand
 *  l'appelant a besoin des stats/fidélité par ailleurs). */
export function renderDiffHtml(
  adapted: ResumeProfile,
  diff: ResumeDiff,
  settings?: RenderSettings | unknown,
  labels: DiffLabels = DEFAULT_LABELS,
): string {
  // On force editable=false : la vue diff n'est jamais éditable.
  const cfg = resolveRenderSettings({ ...(settings as object), editable: false })
  const titles = SECTION_TITLES[cfg.locale]
  const css = buildCss(cfg) + DIFF_CSS

  // `diff.summary` est censé TOUJOURS exister : `computeResumeDiff` (seul
  // chemin d'écriture de la colonne `dossiers.diff`) le pose systématiquement.
  // Mais cette fonction lit un blob JSON venu de SQLite, pas un objet typé à
  // la compilation : une ligne plus ancienne écrite par une version passée du
  // moteur, ou une donnée abîmée, ne fait plus la preuve d'elle-même une fois
  // désérialisée. Sans ce filet, un champ manquant faisait planter TOUTE la
  // page (écran blanc, aucun message) au lieu de simplement rater cette
  // accroche-là. On dégrade : accroche affichée telle quelle, sans surlignage
  // de diff, plutôt que la page entière qui disparaît.
  const summary: SummaryDiff = diff.summary ?? {
    text: adapted.basics?.summary ?? '',
    change: 'unchanged',
    sourceText: adapted.basics?.summary ?? '',
    similarity: 1,
  }

  const legend = `
    <div class="diff-legend">
      <span><i class="diff-chip add"></i>${escapeHtml(labels.added)}</span>
      <span><i class="diff-chip reword"></i>${escapeHtml(labels.reworded)}</span>
      <span><i class="diff-chip removed"></i>${escapeHtml(labels.removed)}</span>
    </div>`

  const header = `
    <header class="resume-header">
      <div class="header-text">
        <h1 class="name">${escapeHtml(adapted.basics.name || 'Nom')}</h1>
        ${adapted.basics.label ? `<p class="label">${escapeHtml(adapted.basics.label)}</p>` : ''}
      </div>
    </header>`

  // Accroche annotée. Masquée si la section l'est dans les réglages de rendu.
  let summaryHtml = ''
  if (cfg.sections.summary && (summary.text || summary.change === 'removed')) {
    if (summary.change === 'added') {
      summaryHtml = `<section class="summary"><p><span class="diff-add">${escapeHtml(summary.text)}</span></p></section>`
    } else if (summary.change === 'reworded') {
      summaryHtml = `<section class="summary"><p><span class="diff-reword" title="${labels.before} ${escapeHtml(summary.sourceText)}">${escapeHtml(summary.text)}</span></p>` +
        `<div class="diff-src"><span class="diff-src-label">${labels.before}</span> ${escapeHtml(summary.sourceText)}</div></section>`
    } else if (summary.change === 'removed') {
      summaryHtml = `<section class="summary"><p><span class="diff-removed">${escapeHtml(summary.sourceText)}</span></p></section>`
    } else {
      summaryHtml = `<section class="summary"><p>${escapeHtml(summary.text)}</p></section>`
    }
  }

  // Compétences : ajoutées surlignées, retirées barrées, le reste neutre.
  // Même filet qu'au-dessus pour `skillsAdded`/`skillsRemoved`/`work` : des
  // tableaux vides plutôt qu'un accès qui plante sur un champ absent.
  const skillsAdded = diff.skillsAdded ?? []
  const skillsRemoved = diff.skillsRemoved ?? []
  const work = diff.work ?? []
  const addedSet = new Set(skillsAdded)
  let skillsHtml = ''
  if (cfg.sections.skills && (adapted.skills.length || skillsRemoved.length)) {
    const kept = adapted.skills.map((s) =>
      addedSet.has(s) ? `<span class="diff-add">${escapeHtml(s)}</span>` : escapeHtml(s),
    )
    const removed = skillsRemoved.map((s) => `<span class="diff-removed">${escapeHtml(s)}</span>`)
    skillsHtml = `
      <section class="section">
        <h2 class="section-title">${titles.skills}</h2>
        <p class="skills">${[...kept, ...removed].join(', ')}</p>
      </section>`
  }

  // Expériences : puces adaptées annotées + puces retirées barrées en dessous.
  const workItems = work.map((wd, wi) => {
    const head = [wd.position, wd.name].filter(Boolean).map(escapeHtml).join(' · ')
    const bullets = wd.bullets.map((b, bi) => renderBullet(b, labels, wi, bi)).join('')
    const removed = wd.removedBullets.map((r) => renderRemoved(r.text)).join('')
    const list = bullets || removed ? `<ul class="bullets">${bullets}${removed}</ul>` : ''
    return `
      <article class="exp-item">
        <div class="exp-head"><span class="exp-title">${head}</span></div>
        ${list}
      </article>`
  }).join('')
  const workHtml = work.length
    ? `<section class="section"><h2 class="section-title">${titles.work}</h2>${workItems}</section>`
    : ''

  const body = [legend, header, summaryHtml, skillsHtml, workHtml].filter(Boolean).join('\n')

  return `<!doctype html>
<html lang="fr">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(documentBaseName({ candidate: adapted.basics.name, company: cfg.company }, 'cv'))}_diff</title>
<style>${css}</style>
<script>${DIFF_SCRIPT}</script>
</head>
<body>
<div class="resume">
${body}
</div>
</body>
</html>`
}
